(() => {
  'use strict';
  if (window.SolsticePersonalization) return;
  const Core = window.SolsticePersonalizationCore;
  const config = JSON.parse(document.getElementById('SolsticePersonalizationConfig')?.textContent || '{}');
  const isEnglish = /^en(?:-|$)/i.test(document.documentElement?.lang || 'fr');
  const copy = (french, english) => config.translations?.[french] ?? (isEnglish ? english : french);
  const root = window.Shopify?.routes?.root || '/';
  const HISTORY = 'solstice_jewelry_history_v1';
  const PREF = 'solstice_finish_v1';
  const read = (key, fallback) => { try { return JSON.parse(localStorage.getItem(key)) ?? fallback; } catch { return fallback; } };
  const write = (key, value) => { try { localStorage.setItem(key, JSON.stringify(value)); } catch {} };
  const favorites = () => read('ear_config_favorites', {});
  const mayTrack = () => window.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.() === true;
  const history = () => mayTrack() ? read(HISTORY, {}) : {};
  const getColor = () => Core.finish(read(PREF, null));
  let applyingColor = false;
  const request = async (url, options = {}) => {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 15000);
    try {
      const res = await fetch(url, {...options, signal: controller.signal});
      const data = await res.json();
      if (!res.ok) throw new Error(data.description || data.error || copy("Veuillez réessayer.","Please try again."));
      return data;
    } finally {clearTimeout(timeout);}
  };
  const getProduct = handle => {
    if (!/^[a-z0-9-]+$/.test(handle)) return Promise.resolve(null);
    return request(`${root}products/${handle}.js`,{cache:'no-store'}).catch(()=>null);
  };
  function rewriteLinks() {
    const color = getColor();
    if (!color) return;
    document.querySelectorAll('a[href*="/collections/"]').forEach(a => {
      if (a.closest('.color-capsule-filter') || /argent|silver|doré|dore|gold/i.test(a.textContent)) return;
      const original = a.dataset.solsticeOriginalHref || a.getAttribute('href');
      a.dataset.solsticeOriginalHref = original;
      a.href = Core.colorURL(original, color, config.pairs || [], location.origin);
    });
  }
  function setColor(value, broadcast = true) {
    const color = Core.finish(value);
    if (!color) return;
    write(PREF, color);
    rewriteLinks();
    if (broadcast) window.dispatchEvent(new CustomEvent('solstice:preference-changed', {detail:{color}}));
  }
  function viewProduct(p) {
    if (!mayTrack() || !p?.id) return;
    const items=history(), id=String(p.product_id || p.id), previous=items[id] || {visits:0,photos:0};
    const handle=String(p.url || '').split('/products/')[1]?.split(/[?#]/)[0];
    items[id]={...previous,handle,visits:previous.visits+1,lastAt:Date.now()};
    write(HISTORY,Object.fromEntries(Object.entries(items).sort((a,b)=>b[1].lastAt-a[1].lastAt).slice(0,80)));
  }
  function getViewed() {
    const result = {};
    for (const [id, item] of Object.entries(history())) {
      if (Date.now() - item.lastAt < 30 * 86400000) result[id] = true;
    }
    return result;
  }
  let trackedPage = false;
  let galleryObserver;
  const photoIds = new Set();
  let galleryUsed = false;
  document.addEventListener('pointerdown', e => {if(e.target.closest('media-gallery, .product__media-list')) galleryUsed = true;}, {passive:true});
  document.addEventListener('scroll', () => {galleryUsed = true;}, {passive:true, capture:true});
  function record(kind, mediaId) {
    const p = config.product;
    if (!p || !mayTrack()) return;
    const items = history();
    const current = items[p.id] || {handle:p.handle, visits:0, photos:0, lastAt:0};
    if (kind === 'visit' && !trackedPage) {current.visits++; trackedPage = true;}
    if (kind === 'photo' && mediaId && !photoIds.has(mediaId)) {photoIds.add(mediaId); current.photos = Math.max(current.photos, photoIds.size);}
    current.lastAt = Date.now();
    items[p.id] = current;
    write(HISTORY, Object.fromEntries(Object.entries(items).sort((a,b) => b[1].lastAt - a[1].lastAt).slice(0,80)));
  }
  function trackGallery() {
    if (!config.product || !mayTrack()) return;
    record('visit');
    if (window.IntersectionObserver && !galleryObserver) {
      galleryObserver = new IntersectionObserver(entries => entries.forEach(entry => {
        if (entry.isIntersecting && galleryUsed && document.visibilityState === 'visible') record('photo', entry.target.id);
      }), {threshold:0.6});
      document.querySelectorAll('[id^="Slide-"][data-media-id], .product__media-list > [id]').forEach(el => galleryObserver.observe(el));
    }
  }
  function applyPreference() {
    // Deferred scripts can run before the product components subscribe to changes.
    if (config.product && document.querySelector('variant-selects') &&
        (!customElements.get('product-info') || !customElements.get('variant-selects') || !customElements.get('product-form'))) {
      Promise.all(['product-info', 'variant-selects', 'product-form'].map(name => customElements.whenDefined(name)))
        .then(applyPreference);
      return;
    }
    const explicit = new URL(location.href).searchParams.get('variant');
    const pair = (config.pairs || []).find(p => p.silver === location.pathname);
    if (pair) setColor('silver', false);
    const p = config.product;
    if (explicit && p) {
      const variant = p.variants.find(v => String(v.id) === explicit);
      const color = variant?.options.map(Core.finish).find(Boolean);
      if (color) setColor(color, false);
    }
    const color = getColor();
    if (!color) return;
    if (config.pageType === 'collection') {
      const destination=Core.colorURL(location.href,color,config.pairs || [],location.origin);
      if (new URL(destination).pathname !== location.pathname) {location.replace(destination);return;}
    }
    applyingColor = true;
    const capsule = document.querySelector('.color-capsule-filter');
    const radio = capsule?.querySelector(`[data-color-family="${color}"]`);
    if (radio) radio.checked = true;
    window.dispatchEvent(new CustomEvent('global-color-switch', {detail:{color}}));
    if (p && !explicit) {
      const input = [...document.querySelectorAll('variant-selects input[type="radio"], .composition-product__colors input')]
        .find(i => Core.finish(i.value) === color);
      if (input && !input.checked) {input.checked = true; input.dispatchEvent(new Event('change',{bubbles:true}));}
      const select = [...document.querySelectorAll('variant-selects select')].find(s => [...s.options].some(o => Core.finish(o.value) === color));
      if (select) {const option = [...select.options].find(o => Core.finish(o.value) === color); if (select.value !== option.value) {select.value = option.value; select.dispatchEvent(new Event('change',{bubbles:true}));}}
    }
    applyingColor = false;
    rewriteLinks();
  }
  document.addEventListener('change', event => {
    if (applyingColor || !event.isTrusted) return;
    const target = event.target;
    if (target.matches('variant-selects input, variant-selects select, .composition-product__colors input, .color-capsule-filter input, .product-card-wrapper input[data-color-family]')) setColor(target.dataset.colorFamily || target.value);
  }, true);
  document.addEventListener('click', event => {
    const link=event.target.closest('a[href*="/collections/"]');
    if(link && /argent|silver|doré|dore|gold/i.test(link.textContent)) {
      const url=new URL(link.href,location.origin),pair=(config.pairs || []).find(p=>p.gold===url.pathname || p.silver===url.pathname);
      if(pair && url.origin===location.origin)setColor(pair.silver===url.pathname?'silver':'gold');
    }
    const media = event.target.closest('[data-media-id], [id^="Slide-"]');
    if (media) record('photo', media.dataset.mediaId || media.id);
  }, true);
  document.addEventListener('visitorConsentCollected', () => {
    if (!mayTrack()) {try {localStorage.removeItem(HISTORY);} catch {} galleryObserver?.disconnect();galleryObserver=null;trackedPage=false;photoIds.clear();}
    else trackGallery();
  });
  window.addEventListener('favorites-updated', () => window.dispatchEvent(new Event('solstice:interest-changed')));
  const catalog = () => request(`${root}collections/configurator-all?view=solstice-selection&sort_by=best-selling`,{cache:'no-store'});
  const readyVariants = (product,entry) => {
    const ready = new Set((entry?.ready_variants || []).map(String));
    return (product?.variants || []).filter(variant=>variant.available && variant.price>0 && ready.has(String(variant.id)));
  };
  function colorVariants(p, color) {
    const option = p.options.findIndex(o => /colou?r|couleur|finition/i.test(typeof o === 'string' ? o : o.name));
    return p.variants.filter(v => v.available && v.price > 0 && (option < 0 || Core.finish(v.options[option]) === color));
  }
  function visitorId() {
    let id = read('solstice_offer_visitor_v1', null);
    if (!/^[a-f0-9-]{36}$/.test(id || '')) {
      const bytes=crypto.getRandomValues(new Uint8Array(16));bytes[6]=(bytes[6]&15)|64;bytes[8]=(bytes[8]&63)|128;
      id=Array.from(bytes,b=>b.toString(16).padStart(2,'0')).join('').replace(/^(.{8})(.{4})(.{4})(.{4})(.{12})$/,'$1-$2-$3-$4-$5');
      write('solstice_offer_visitor_v1',id);
    }
    return id;
  }
  async function quote(variantId) {
    if (!config.offerEndpoint || (window.Shopify?.currency?.active || 'EUR') !== 'EUR') return null;
    const key = `solstice_offer_${variantId}`;
    const prior = read(key, null);
    if (prior && prior.expiresAt <= Date.now()) return null;
    if (prior && prior.expiresAt > Date.now() + 5000) return prior;
    const offer = await request(config.offerEndpoint, {method:'POST', headers:{'Content-Type':'application/json','X-Solstice-Checkout-Version':'2'},body:JSON.stringify({variantId:String(variantId),visitorId:visitorId()})}).catch(() => null);
    if (offer?.expiresAt) write(key, offer);
    return offer?.eligible ? offer : null;
  }
  function tokensForCart(cart) {
    const accepted = [];
    for (const item of cart.items || []) {
      const token = item.properties?._solstice_offer;
      const code = item.properties?._solstice_offer_code;
      const expiresAt = Number(item.properties?._solstice_offer_expires);
      if (!token || !/^SC-[A-F0-9]{32}$/.test(code || '') || expiresAt <= Date.now()) continue;
      // Native Shopify product discounts must never reach another, unmarked line of this product.
      if (cart.items.some(other => String(other.product_id) === String(item.product_id) && other.properties?._solstice_offer_code !== code)) continue;
      accepted.push({variantId:String(item.variant_id), token, code});
    }
    return accepted.slice(0,4);
  }
  let syncing;
  async function syncOffers(cart) {
    if (syncing) return syncing;
    syncing = (async () => {
      cart ||= await request(`${root}cart.js`);
      const offerCodes = tokensForCart(cart).map(o => o.code);
      const otherCodes = (cart.discount_codes || []).filter(d => !/^SC-/.test(d.code)).map(d => d.code);
      const desired = [...new Set([...otherCodes, ...offerCodes])].slice(0,5);
      const existing = (cart.discount_codes || []).map(d => d.code);
      if ([...desired].sort().join(',') !== [...existing].sort().join(',')) {
        cart = await request(`${root}cart/update.js`, {method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({discount:desired.join(',')})});
      }
      return cart;
    })().finally(() => {syncing = null;});
    return syncing;
  }
  const money = cents => new Intl.NumberFormat(isEnglish ? 'en-GB' : 'fr-FR',{style:'currency',currency:window.Shopify?.currency?.active || 'EUR'}).format(cents / 100);
  const text = (tag, value, cls) => {const el=document.createElement(tag);el.textContent=value;if(cls)el.className=cls;return el;};
  class CartSuggestions extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;
      this.bound = true;
      this.onOpen = e => {if(e.detail) this.refresh();};
      this.onUpdate = () => {if(this.isConnected) this.refresh();};
      window.addEventListener('cart-visibility-changed', this.onOpen);
      window.addEventListener('cart:updated', this.onUpdate);
      // Vue mounts the drawer after its first open event has already fired.
      this.refresh();
      this.clock = setInterval(() => this.querySelectorAll('[data-offer-expires]').forEach(card => {
        if (+card.dataset.offerExpires <= Date.now()) {
          const note=card.querySelector('.solstice-upsell__offer');if(note)note.textContent='';
          card.querySelector('.solstice-upsell__price').textContent = money(+card.dataset.price);
          card.dataset.offerExpires = '';
        }
      }), 15000);
    }
    disconnectedCallback() {window.removeEventListener('cart-visibility-changed',this.onOpen);window.removeEventListener('cart:updated',this.onUpdate);clearInterval(this.clock);this.bound=false;this.version=(this.version || 0)+1;}
    showStatus(loading) {
      const section=text('section','','solstice-upsell');section.setAttribute('aria-label',copy("Complétez votre compo","Complete your ear stack"));
      section.append(text('div',copy("Complétez votre compo","Complete your ear stack"),'solstice-upsell__heading'));
      const message=text('p','','solstice-upsell__status');
      if(loading){message.textContent=copy("Chargement de votre sélection…","Loading your selection…");message.setAttribute('role','status');}
      else {
        const browse=text('a',copy("Découvrir les bijoux","Browse jewellery"));browse.href=Core.colorURL(`${root}collections/piercing-oreille`,getColor(),config.pairs || [],location.origin);
        const retry=text('button',copy("Réessayer","Try again"),'solstice-upsell__add');retry.type='button';retry.onclick=()=>{this.replaceChildren();this.refresh();};
        message.append(browse,retry);
      }
      section.append(message);this.replaceChildren(section);this.hidden=false;
    }
    async refresh() {
      const version = this.version = (this.version || 0) + 1;
      this.showStatus(true);
      try {
        const cart = await request(`${root}cart.js`);
        if(this.version !== version)return;
        if (!cart.item_count) {this.replaceChildren();this.hidden=true;return;}
        const color = getColor() || (cart.items.filter(i => Core.finish(i.variant_title) === 'silver').length > cart.items.length / 2 ? 'silver' : 'gold');
        const products = (await catalog()).filter(product=>product.ready_variants?.length || cart.items.some(item=>String(item.product_id)===String(product.id)));
        const {primary,fallback}=Core.cartCandidates(products,cart.items,favorites(),history());
        const hydrate=async item => {
          const p=await getProduct(item.handle);if(!p)return null;
          const product={...p,variants:readyVariants(p,item)};
          const variants=colorVariants(product,color);if(!variants.length)return null;
          return {product,variants,discountEligible:item.discountEligible,offer:null};
        };
        const cards=(await Promise.all(primary.map(hydrate))).filter(Boolean).slice(0,5);
        // No browsing history is required to display the cart's recommendation block.
        for(let i=0;cards.length<5 && i<fallback.length;i+=5){
          cards.push(...(await Promise.all(fallback.slice(i,i+5).map(hydrate))).filter(Boolean).slice(0,5-cards.length));
        }
        if(this.version !== version) return;
        if(!cards.length){this.showStatus(false);return;}
        this.render(cards, color);
      } catch {if(this.version===version)this.showStatus(false);}
    }
    render(cards, color) {
      this.replaceChildren();this.hidden=!cards.length;if(!cards.length)return;
      const section=text('section','','solstice-upsell');section.setAttribute('aria-label',copy("Complétez votre compo","Complete your ear stack"));
      const heading=text('div','','solstice-upsell__heading');heading.append(text('span',copy("Complétez votre compo","Complete your ear stack")));
      const arrows=text('div','','solstice-upsell__arrows'), track=text('div','','solstice-upsell__track');
      let activeCard=0;
      const navigation=[];
      const dots=text('div','','solstice-upsell__dots'),dotButtons=[];
      dots.style.cssText='display:flex;justify-content:center;gap:2px;margin:4px 0';
      if(cards.length<2)dots.style.display='none';
      const selectCard=index=>{
        activeCard=Math.max(0,Math.min(cards.length-1,index));
        track.style.setProperty('--solstice-slide',String(activeCard));
        [...track.children].forEach((card,i)=>{
          card.inert=i!==activeCard;card.setAttribute('aria-hidden',String(i!==activeCard));
          card.querySelectorAll('a,button').forEach(control=>{control.tabIndex=i===activeCard?0:-1;});
        });
        navigation.forEach(({button,dir})=>{button.disabled=dir<0?activeCard===0:activeCard===cards.length-1;});
        dotButtons.forEach((button,i)=>{
          button.setAttribute('aria-pressed',String(i===activeCard));
          button.firstElementChild.style.opacity=i===activeCard?'1':'.25';
        });
      };
      cards.forEach((_,i)=>{
        const button=text('button',''),dot=text('span','');button.type='button';
        button.setAttribute('aria-label',`${i+1} / ${cards.length}`);
        button.style.cssText='display:grid;place-items:center;width:28px;height:28px;padding:0;border:0;background:transparent;cursor:pointer';
        dot.style.cssText='display:block;width:6px;height:6px;border-radius:50%;background:#765339';
        button.append(dot);button.onclick=()=>selectCard(i);dotButtons.push(button);dots.append(button);
      });
      let gesture=null,suppressClickUntil=0;
      const resetDrag=()=>{gesture=null;track.classList.remove('is-dragging');track.style.removeProperty('--solstice-drag');};
      track.addEventListener('pointerdown',event=>{
        if(cards.length<2 || !event.isPrimary || event.button!==0)return;
        gesture={id:event.pointerId,x:event.clientX,y:event.clientY,locked:false};
      });
      track.addEventListener('pointermove',event=>{
        if(!gesture || gesture.id!==event.pointerId)return;
        const dx=event.clientX-gesture.x,dy=event.clientY-gesture.y;
        if(!gesture.locked){
          if(Math.max(Math.abs(dx),Math.abs(dy))<8)return;
          if(Math.abs(dy)>=Math.abs(dx)){resetDrag();return;}
          gesture.locked=true;track.setPointerCapture(event.pointerId);track.classList.add('is-dragging');
        }
        event.preventDefault();
        const atEdge=(activeCard===0 && dx>0)||(activeCard===cards.length-1 && dx<0);
        track.style.setProperty('--solstice-drag',`${atEdge?dx*.2:dx}px`);
      });
      track.addEventListener('pointerup',event=>{
        if(!gesture || gesture.id!==event.pointerId)return;
        const dx=event.clientX-gesture.x,dragged=gesture.locked;
        resetDrag();
        if(dragged){
          suppressClickUntil=Date.now()+350;
          if(Math.abs(dx)>40)selectCard(activeCard+(dx<0?1:-1));
        }
        if(track.hasPointerCapture(event.pointerId))track.releasePointerCapture(event.pointerId);
      });
      track.addEventListener('pointercancel',resetDrag);
      // Touch starts with implicit capture on the child; moving capture to the track
      // must not cancel the gesture when that child's lost-capture event bubbles.
      track.addEventListener('lostpointercapture',event=>{if(event.target===track)resetDrag();});
      track.addEventListener('dragstart',event=>event.preventDefault());
      track.addEventListener('click',event=>{if(Date.now()<suppressClickUntil){event.preventDefault();event.stopImmediatePropagation();}},true);
      // Ignore a stroke's decaying momentum, but accept a renewed impulse immediately.
      // Waiting for silence here makes consecutive trackpad strokes feel unresponsive.
      let wheelAmount=0,wheelDirection=0,wheelMoved=false,wheelPeak=0,wheelLast=0,wheelTime=0,wheelDecaying=false;
      section.addEventListener('wheel',event=>{
        if(cards.length<2 || event.ctrlKey || Math.abs(event.deltaX)<=Math.abs(event.deltaY))return;
        event.preventDefault();
        const direction=Math.sign(event.deltaX);
        const magnitude=Math.abs(event.deltaX)*(event.deltaMode===1?16:event.deltaMode===2?track.clientWidth:1);
        const gap=event.timeStamp-wheelTime;
        const renewedImpulse=wheelMoved && (
          (wheelDecaying && magnitude>=12 && magnitude>=wheelLast*1.6 && magnitude-wheelLast>=4) ||
          (gap>=90 && magnitude>=Math.max(36,wheelPeak*.8) && magnitude>=wheelLast)
        );
        if(direction!==wheelDirection || gap>160 || renewedImpulse){
          wheelAmount=0;wheelMoved=false;wheelPeak=0;wheelDecaying=false;wheelDirection=direction;
        }
        if(magnitude<wheelPeak*.65)wheelDecaying=true;
        wheelPeak=Math.max(wheelPeak,magnitude);wheelLast=magnitude;wheelTime=event.timeStamp;
        if(wheelMoved)return;
        wheelAmount+=magnitude;
        if(wheelAmount>=36){selectCard(activeCard+direction);wheelMoved=true;wheelAmount=0;}
      },{passive:false});
      if(cards.length===1){arrows.hidden=true;track.classList.add('solstice-upsell__track--single');}
      for(const [label,dir,symbol] of [[copy("Bijou précédent","Previous piece"),-1,'‹'],[copy("Bijou suivant","Next piece"),1,'›']]){const b=text('button',symbol);b.type='button';b.setAttribute('aria-label',label);b.onclick=()=>selectCard(activeCard+dir);navigation.push({button:b,dir});arrows.append(b);}
      heading.append(arrows);section.append(heading,track,dots);const status=text('p','','solstice-upsell__status');status.setAttribute('role','status');section.append(status);
      cards.forEach(data => {
        let variant=data.variants[0],offer=data.offer,selectionVersion=0,adding=false;
        const available=data.product.variants.filter(v=>v.available && v.price>0);
        const optionNames=data.product.options.map(o=>typeof o==='string'?o:o.name);
        const colorIndex=optionNames.findIndex(name=>/colou?r|couleur|finition/i.test(name));
        const optionButtons=[];
        const matchingVariant=(index,value)=>{
          const exact=available.find(v=>v.options[index]===value && v.options.every((option,i)=>i===index || option===variant.options[i]));
          return exact || (index===colorIndex ? available.find(v=>v.options[index]===value) : null);
        };
        const card=text('article','','solstice-upsell__card');const link=text('a','');link.draggable=false;link.className='solstice-upsell__thumbnail';const img=document.createElement('img');img.className='solstice-upsell__image';img.alt=data.product.title;img.loading='lazy';img.draggable=false;link.append(img);
        const body=text('div','','solstice-upsell__body'), name=text('a',data.product.title,'solstice-upsell__name');
        const price=text('div','','solstice-upsell__price'), note=text('p','','solstice-upsell__offer');
        const add=text('button',copy("Ajouter au panier","Add to bag"),'solstice-upsell__add');add.type='button';
        body.append(name,price,note);
        const update=()=>{
          const url=`${root}products/${data.product.handle}?variant=${variant.id}`;link.href=name.href=url;
          img.src=variant.featured_image?.src || data.product.featured_image;
          price.replaceChildren();card.dataset.price=String(variant.price);
          if(offer?.eligible && offer.expiresAt>Date.now()) {
            price.append(text('s',money(variant.price)),text('span',money(variant.price-Math.floor(variant.price*.1))));note.textContent=copy("−10 % aujourd’hui sur ce bijou","10% off this piece today");card.dataset.offerExpires=offer.expiresAt;
          } else {price.textContent=money(variant.price);note.textContent='';delete card.dataset.offerExpires;}
          optionButtons.forEach(({button,index,value})=>{
            button.setAttribute('aria-pressed',String(variant.options[index]===value));
            button.disabled=adding || !matchingVariant(index,value);
          });
        };
        optionNames.forEach((optionName,index)=>{
          const values=[...new Set(data.product.variants.map(v=>v.options[index]))].filter(Boolean);
          if(optionName==='Title' && values.length===1 && values[0]==='Default Title')return;
          if(/diam[eè]tre|diameter|longueur|length|tige/i.test(optionName))values.sort((a,b)=>parseFloat(a.replace(',','.'))-parseFloat(b.replace(',','.')));
          const label=index===colorIndex?copy("Coloris","Colour"):/diam[eè]tre/i.test(optionName)?copy("Diamètre de l’anneau","Hoop diameter"):optionName;
          const group=text('fieldset','','solstice-upsell__option-group');group.append(text('legend',`${label} :`));
          const choices=text('div','','solstice-upsell__choices');group.append(choices);
          values.forEach(value=>{
            const button=text('button',value,'solstice-upsell__option-button');button.type='button';
            const finish=index===colorIndex?Core.finish(value):null;
            if(finish){
              button.classList.add('solstice-upsell__option-button--swatch');
              button.dataset.finish=finish;button.title=value;
              button.replaceChildren(text('span',value,'visually-hidden'));
            }
            optionButtons.push({button,index,value});choices.append(button);
            button.onclick=async()=>{
              if(adding)return;
              const next=matchingVariant(index,value);if(!next || next.id===variant.id)return;
              const version=++selectionVersion;
              variant=next;offer=null;add.disabled=true;update();
              if(index===colorIndex)setColor(value);
              const nextOffer=data.discountEligible?await quote(next.id).catch(()=>null):null;
              if(version!==selectionVersion)return;
              offer=nextOffer;add.disabled=false;update();
            };
          });
          body.append(group);
        });
        add.onclick=async()=>{
          adding=true;add.disabled=true;status.textContent='';update();
          try {
            const cart=await request(`${root}cart.js`);
            if(cart.items.some(i=>String(i.product_id)===String(data.product.id))){status.textContent=copy("Ce bijou est déjà dans votre panier.","This piece is already in your bag.");return;}
            const [freshProduct,freshCatalog]=await Promise.all([getProduct(data.product.handle),catalog()]);
            const entry=freshCatalog.find(product=>String(product.id)===String(data.product.id));
            if(!readyVariants(freshProduct,entry).some(candidate=>String(candidate.id)===String(variant.id))){await this.refresh();return;}
            const active=offer?.eligible && offer.expiresAt>Date.now();
            const properties=active?{_solstice_offer:offer.token,_solstice_offer_code:offer.code,_solstice_offer_expires:String(offer.expiresAt)}:{};
            await request(`${root}cart/add.js`,{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({items:[{id:variant.id,quantity:1,properties}]})});
            const updated=await syncOffers();
            const discounted=updated.items.some(i=>String(i.variant_id)===String(variant.id)&&i.final_line_price<i.original_line_price);
            status.textContent=active&&!discounted?copy("Bijou ajouté. La remise ne se cumule pas avec une autre offre ; vérifiez le total du panier.","Piece added. This discount cannot be combined with another offer; check your bag total."):copy("Bijou ajouté à votre composition.","Piece added to your ear stack.");
            window.dispatchEvent(new Event('cart:updated'));
            window.dispatchEvent(new CustomEvent('solstice:conversion:cart-added'));
          } catch {status.textContent=copy("Ajout impossible pour le moment. Votre panier est conservé.","We could not add this piece just now. Your bag has been kept.");}
          finally {adding=false;add.disabled=false;update();}
        };
        body.append(add);card.append(link,body);track.append(card);update();
        // Show products immediately, even when the discount service is slow on mobile.
        if(data.discountEligible){
          const version=selectionVersion;add.disabled=true;
          quote(variant.id).catch(()=>null).then(nextOffer=>{
            if(version!==selectionVersion || !card.isConnected)return;
            offer=nextOffer;add.disabled=false;update();
          });
        }
      });
      selectCard(0);
      this.append(section);
    }
  }
  customElements.define('solstice-cart-suggestions',CartSuggestions);
  window.SolsticePersonalization={getColor,setColor,getViewed,viewProduct,getFavorites:favorites,rank:(list,selected)=>Core.alternate(list,getViewed(),favorites(),selected),syncOffers,tokensForCart};
  document.addEventListener('submit', async event => {
    const form=event.target, submitter=event.submitter;
    if (submitter?.name !== 'checkout' || !/\/cart\/?$/.test(new URL(form.action,location.origin).pathname)) return;
    event.preventDefault();
    submitter.disabled=true;
    try {await syncOffers();location.assign(`${root}checkout`);}
    catch {submitter.disabled=false;const error=text('p',copy("Le panier doit être actualisé avant le paiement. Veuillez réessayer.","Your bag needs to be updated before checkout. Please try again."));error.setAttribute('role','alert');form.append(error);}
  },true);
  const start=()=>{
    applyPreference();trackGallery();
    if (!window.Shopify?.customerPrivacy && typeof window.Shopify?.loadFeatures === 'function') {
      window.Shopify.loadFeatures([{name:'consent-tracking-api',version:'0.1'}],error=>{if(!error)trackGallery();});
    }
  };
  if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',start,{once:true});else start();
  window.addEventListener('pageshow',rewriteLinks);
  document.addEventListener('shopify:section:load',applyPreference);
})();
